"use client";
import { isEmpty } from "lodash-es";
import type { Ref } from "react";
import { Flex } from "@ecco/ui/flex";
import { PagePath } from "@components/cms/link/PagePath";
import { ModalLink } from "@components/cms/link/ModalLink";
import type { TextProps } from "@ecco/ui/text";
import { ExternalUrl } from "@components/cms/link/ExternalUrl";
import type { LinkProps } from "@ecco/ui/link";
import { Text } from "@ecco/ui/text";
import { InternalLink } from "@components/cms/link/InternalLink";
import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import { resolveLinkExternalHref } from "@contentful-api/helpers/resolveLinkExternalHref";
import type { NextLinkProps } from "@components/ui/next-link/NextLink";

export type ILinkProps = Omit<
    Partial<NextLinkProps> & Partial<LinkProps> & Partial<TextProps>,
    "ref"
> & {
    link: ILink | IPagePath;
    children?: React.ReactNode;
    ref?: Ref<HTMLElement & HTMLParagraphElement & HTMLAnchorElement & HTMLDivElement>;
};

type UnknownLinkProps = {
    sx?: TextProps["sx"];
    ref?: Ref<HTMLDivElement>;
};

const getLinkType = (link: ILink | IPagePath) => {
    if (link?.sys?.contentType?.sys?.id === "pagePath") {
        return "pagePath";
    }

    const _link = link as ILink;

    const modal = _link?.fields?.modal;

    if (!isEmpty(modal)) {
        return "modal";
    }

    const internalLink = _link?.fields?.internalLink ?? _link?.fields?.deepLink;

    if (!isEmpty(internalLink)) {
        return "internalLink";
    }

    const fields = _link?.fields;
    if (fields && resolveLinkExternalHref(fields)) {
        return "externalUrl";
    }

    return "unknown";
};

const UnknownLink = ({ sx, ref }: UnknownLinkProps) => (
    <Flex w="full" justifyContent="start" ref={ref}>
        <Text
            sx={{
                ...sx,
                color: "missingLinkRed",
                fontWeight: "bold",
            }}
        >
            Missing link type
        </Text>
    </Flex>
);

UnknownLink.displayName = "UnknownLink";

const linksMap = {
    modal: ModalLink,
    internalLink: InternalLink,
    externalUrl: ExternalUrl,
    pagePath: PagePath,
    unknown: UnknownLink,
};

export const Link = ({ link, ref, ...rest }: ILinkProps) => {
    if (isEmpty(link?.fields)) return null;

    const type = getLinkType(link);
    const Component = linksMap[type];
    // Have to cast ref because of different ref types in the linksMap
    return <Component link={link} ref={ref as any} {...rest} />;
};

Link.displayName = "Link";
